import { useState, useEffect, useRef } from 'react'
import { DropDownList } from "@progress/kendo-react-dropdowns"
import moment from 'moment'
import dompurify from 'dompurify'
import { Dialog } from '@progress/kendo-react-dialogs'
import IFrame from './iframe'
import { SvgIcon } from '@progress/kendo-react-common'
import * as svgIcon from '@progress/kendo-svg-icons'
import { SearchBarHLPure, SimpleLoading } from '../../pures'

const ConversationList = ({ dataConversations, t, chatbotUrl, handleGetPaginatedConversation, isLoadingChangePage, searchValue, handleSearchChange }) => {
  // Opciones de ordenamiento
  const sortOptions = [
    { text: t('sortByRecent'), value: 'recent' },
    { text: t('sortByOldest'), value: 'oldest' },
    { text: t('sortByMostMessages'), value: 'mostMessages' },
    { text: t('sortByLeastMessages'), value: 'leastMessages' },
    { text: t('sortByMostTokens'), value: 'mostTokens' },
    { text: t('sortByLeastTokens'), value: 'leastTokens' }
  ]

  const [selectedSort, setSelectedSort] = useState(sortOptions[0])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [showChatModal, setShowChatModal] = useState(false)
  const [iframeUrl, setIframeUrl] = useState('')
  const sanitizer = dompurify.sanitize
  // The initial load arrives sorted DEFAULT_SORT in history.action
  const isFirstSortRender = useRef(true)

  useEffect(() => {
    if (isFirstSortRender.current) {
      isFirstSortRender.current = false
      return
    }
    try {
      if (selectedSort.value) {
        handleGetPaginatedConversation(dataConversations.page, 10, selectedSort.value)
      }
    } catch (error) {
      console.error(`effect error in selectedSort: ${error}`)
    }
  }, [selectedSort])
  
  const formatDate = (dateString) => {
    return moment(dateString).format('DD/MM/YYYY HH:mm')
  }
  
  const showDetails = (conversation) => {
    setIframeUrl(`${chatbotUrl}?botId=${conversation.botConfigId}&oldConversationId=${conversation.conversationId}`)
    setSelectedConversation(conversation)
    setShowChatModal(true)
  }
  
  const closeDetails = () => {
    setSelectedConversation(null)
  }

  const getMatchedMessages = (conversation) => {
    if (!Array.isArray(conversation?.matchedMessages)) return []

    return conversation.matchedMessages.filter(message => message?.highlightedMessage || message?.message)
  }

  return (
    <div className="conversation-list-container">
      {/* Header con título y ordenamiento */}
      <div className="conversation-list-header">
        <h2>{t('conversationsList')}</h2>
        
        <div className="sort-dropdown">
          <DropDownList 
            data={sortOptions}
            textField="text"
            dataItemKey="value"
            value={selectedSort}
            onChange={(e) => setSelectedSort(e.value)}
          />
        </div>
      </div>

      <div className="conversation-search-bar">
        <SearchBarHLPure
          placeholder={t('searchPlaceholder')}
          onSearchChange={handleSearchChange}
          searchValue={searchValue}
        />
      </div>

      {/* Lista de conversaciones */}
      <div className="conversation-items">
        {
          isLoadingChangePage &&
          <div className='history-loading'>
            <SimpleLoading />
          </div>
        }
        <div className={isLoadingChangePage && 'conversation-opacity'}>
          {dataConversations.conversations.map(conversation => (
            <div key={conversation.conversationId} className="conversation-item">
              <div className="conversation-header">
                <div className="conversation-info">
                  <div>
                    <span className="conversation-date">{formatDate(conversation.createdAt)}</span>
                    <span className="conversation-id">ID: {conversation.conversationId}</span>
                  </div>
                  <div className="conversation-stats">
                    <span>{t('messages')}: {conversation.totalMessages}</span>
                    <span>{t('tokens')}: {conversation.totalTokens}</span>
                    {conversation.phoneNumber && <span>{t('phoneNumber')}: {conversation.phoneNumber}</span>}
                  </div>
                </div>
                <button
                  className="details-button"
                  onClick={() => showDetails(conversation)}
                >
                  {t('viewDetails')}
                </button>
              </div>
              {getMatchedMessages(conversation).length > 0 && (
                <div className="conversation-matched-messages">
                  <span className="conversation-matched-title">{t('matchedMessagesTitle')}</span>
                  <div className="conversation-matched-list">
                    {getMatchedMessages(conversation).map((matchedMessage, index) => (
                      <div key={`${conversation.conversationId}-matched-${index}`} className="conversation-matched-item">
                        <span className="conversation-matched-date">{formatDate(matchedMessage.createdDate)}</span>
                        <div
                          className="conversation-matched-text"
                          dangerouslySetInnerHTML={{
                            __html: sanitizer(matchedMessage.highlightedMessage || matchedMessage.message)
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          {/* pagination buttons */}
          {
            dataConversations.conversations.length > 0 &&
            <div className='pagination'>
              <p>{t('pagination.subtitle', {page: dataConversations.page, registers: dataConversations.total})}</p>
              <div className={`arrow ${dataConversations.page !== 1 ? 'arrowEnabled' : 'arrowDisabled'}`} onClick={() => {
                if (dataConversations.page !== 1 && !isLoadingChangePage) {
                  handleGetPaginatedConversation(1, 10, selectedSort.value)
                }
              }}>
                <SvgIcon icon={svgIcon['chevronDoubleLeftIcon']} size="large" />
              </div>
              <div className={`arrow ${dataConversations.page !== 1 ? 'arrowEnabled' : 'arrowDisabled'}`} onClick={() => {
                if (dataConversations.page !== 1 && !isLoadingChangePage) {
                  handleGetPaginatedConversation(dataConversations.page - 1, 10, selectedSort.value)
                }
              }}>
                <SvgIcon icon={svgIcon['chevronLeftIcon']} size="large" />
              </div>
              <div className={`arrow ${dataConversations.page !== dataConversations.totalPages ? 'arrowEnabled' : 'arrowDisabled'}`} onClick={() => {
                if (dataConversations.page !== dataConversations.totalPages && !isLoadingChangePage) {
                  handleGetPaginatedConversation(dataConversations.page + 1, 10, selectedSort.value)
                }
              }}>
                <SvgIcon icon={svgIcon['chevronRightIcon']} size="large" />
              </div>
              <div className={`arrow ${dataConversations.page !== dataConversations.totalPages ? 'arrowEnabled' : 'arrowDisabled'}`} onClick={() => {
                if (dataConversations.page !== dataConversations.totalPages && !isLoadingChangePage) {
                  handleGetPaginatedConversation(dataConversations.totalPages, 10, selectedSort.value)
                }
              }}>
                <SvgIcon icon={svgIcon['chevronDoubleRightIcon']} size="large" />
              </div>
            </div>
          }
        </div>
      </div>

      {/* Modal del chatbot */}
      {showChatModal && selectedConversation && (
        <Dialog
          title={selectedConversation.phoneNumber ? `${t('telephone')}: ${selectedConversation.phoneNumber}` : `Chat: ${selectedConversation.conversationId}`}
          onClose={closeDetails}
          width='55%'
          height='70%'
        >
          <IFrame height='100%' width='100%' url={iframeUrl} />
        </Dialog>
      )}

      {/* Estilos para el modal (puedes mover esto a tu archivo CSS) */}
      <style jsx>{`
        .conversation-details-modal {
          position: fixed
          top: 0
          left: 0
          right: 0
          bottom: 0
          background-color: rgba(0,0,0,0.5)
          display: flex
          align-items: center
          justify-content: center
          z-index: 1000
        }
        .modal-content {
          background: white
          border-radius: 4px
          width: 80%
          max-width: 800px
          max-height: 90vh
          overflow: auto
        }
      `}</style>
    </div>
  )
}

export default ConversationList